import { useEffect } from "react";
import { isLevelUpCurrencyCode } from "store/liveOps/loyalty/levels/moneyReward";
import styles from "../../../loyaltyProgram/LiveOpsLoyaltyLevelEditModal.module.scss";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import SwitchBtn from "components/buttons/SwitchBtn";
import { ImageUpload } from "components/imageUpload";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store";
import cn from "classnames";
import {
  handleUpdateFields,
  getLoyaltyRewardCurrencies,
} from "store/liveOps/loyalty/levels/slice";

interface Props {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  isReadOnly: boolean;
}

const LoyaltyProgramLevelsMainTab: React.FC<Props> = ({
  fieldErrors,
  clearFieldError,
  isReadOnly,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const payload = useAppSelector((store) => store["loyalty-levels"].payload);
  const currencies = useAppSelector(
    (store) => store["loyalty-levels"].rewardCurrencies,
  );
  useEffect(() => {
    dispatch(getLoyaltyRewardCurrencies());
  }, [dispatch]);

  const handleData = (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => {
    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: img ?? value }));
  };

  return (
    <div className="bonus-content levels-content">
      <div className="bonus-form-content bonus-main-form-content">
        <div className="bonus-form-box">
          <div className="bonus-form-box--item">
            <InnerInput
              value={payload?.levelNumber?.toString() ?? ""}
              placeholder={t("levelNumber")}
              label={t("levelNumber")}
              type="number"
              id="loyalty-level-levelNumber"
              onChange={(e) => {
                handleData("levelNumber", e);
                clearFieldError("levelNumber");
              }}
              hasError={Boolean(fieldErrors?.["levelNumber"])}
              disabled={isReadOnly}
            />
          </div>

          <div className="bonus-form-box--item">
            <InnerInput
              value={payload?.title ?? ""}
              placeholder={t("enterTitle")}
              label={t("title")}
              type="text"
              id="loyalty-level-title"
              onChange={(e) => {
                handleData("title", e);
                clearFieldError("title");
              }}
              hasError={Boolean(fieldErrors?.["title"])}
              disabled={isReadOnly}
            />
          </div>

          <div className="bonus-form-box--item">
            <InnerInput
              maxCodePoints={2500}
              value={payload?.description ?? ""}
              placeholder={t("enterText")}
              label={t("description")}
              type="textarea"
              id="loyalty-level-description"
              onChange={(e) => {
                handleData("description", e);
                clearFieldError("description");
              }}
              hasError={Boolean(fieldErrors?.["description"])}
              disabled={isReadOnly}
            />
          </div>

          <div className="bonus-form-box--item">
            <InnerInput
              value={payload?.valueFrom ?? ""}
              placeholder={t("pointsFrom")}
              label={t("pointsFrom")}
              type="number"
              id="loyalty-level-valueFrom"
              onChange={() => undefined}
              disabled
              tooltipText={t("pointsFromTooltip")}
              tooltipPosition="top"
            />
          </div>

          <div className="bonus-form-box--item">
            <InnerInput
              value={payload?.pointsRequired ?? ""}
              placeholder={t("pointsRequired")}
              label={t("pointsRequired")}
              type="number"
              id="loyalty-level-pointsRequired"
              onChange={(e) => {
                handleData("pointsRequired", e);
                clearFieldError("pointsRequired");
              }}
              hasError={Boolean(fieldErrors?.["pointsRequired"])}
              disabled={isReadOnly}
              tooltipText={t("pointsRequiredTooltip")}
              tooltipPosition="top"
            />
          </div>

          <div className={cn("bonus-form-box--item", styles.rewardMoney)}>
            <InnerInput
              value={
                payload?.totalLevelUpAmount == null
                  ? "0"
                  : String(payload.totalLevelUpAmount)
              }
              placeholder={t("totalLevelUp")}
              label={t("totalLevelUp")}
              type="text"
              inputMode="decimal"
              id="loyalty-level-totalLevelUpAmount"
              onChange={(e) => {
                handleData("totalLevelUpAmount", e);
                clearFieldError("totalLevelUpAmount");
              }}
              hasError={Boolean(fieldErrors?.["totalLevelUpAmount"])}
              disabled={isReadOnly}
              tooltipText={t("totalLevelUpTooltip")}
              tooltipPosition="top"
            />
            <DefaultDropdown
              ariaLabel={t("totalLevelUpCurrency")}
              value={payload?.totalLevelUpCurrency ?? ""}
              placeholder={t("currency")}
              options={currencies
                .filter(isLevelUpCurrencyCode)
                .map((currency) => ({
                  value: currency,
                  label: currency,
                }))}
              disabled={isReadOnly}
              hasError={Boolean(fieldErrors?.["totalLevelUpCurrency"])}
              className={styles.rewardCurrency}
              isEllipsis
              menuMaxHeight={180}
              onSelect={(currency) => {
                handleData("totalLevelUpCurrency", String(currency));
                clearFieldError("totalLevelUpCurrency");
              }}
            />
          </div>
        </div>

        <div className="bonus-form-box">
          <div className="loyalty-level-assets bonus-form-img">
            <div className="bonus-images">
              <div
                className={cn("bonus-image-item desktop", {
                  "has-error": fieldErrors?.["image"],
                })}
              >
                <ImageUpload
                  label="Desktop"
                  labelClassName="bonus-main-form-label"
                  image={payload?.image}
                  size="large"
                  onAddImage={(file) => {
                    handleData("image", "", file);
                    handleData("removeDesktopImage", false);
                    clearFieldError("image");
                  }}
                  onDeleteImage={() => {
                    handleData("image", "");
                    handleData("removeDesktopImage", true);
                  }}
                  hasDelete
                  isActive={Boolean(payload?.isActive)}
                  disabled={isReadOnly}
                />
              </div>

              <div className="bonus-images-secondary">
                <div className="bonus-image-item mobile">
                  <ImageUpload
                    label="Mobile"
                    labelClassName="bonus-main-form-label"
                    image={payload?.mobileImage}
                    size="large"
                    type="landscape"
                    aspect="21:9"
                    onAddImage={(file) => {
                      handleData("mobileImage", "", file);
                      handleData("removeMobileImage", false);
                      clearFieldError("mobileImage");
                    }}
                    onDeleteImage={() => {
                      handleData("mobileImage", "");
                      handleData("removeMobileImage", true);
                    }}
                    hasDelete
                    isActive={Boolean(payload?.isActive)}
                    disabled={isReadOnly}
                    hasError={Boolean(fieldErrors?.["mobileImage"])}
                  />
                </div>

                <div className="bonus-image-item bonus-portrait">
                  <ImageUpload
                    label="Portrait"
                    labelClassName="bonus-main-form-label"
                    image={payload?.portraitImage}
                    size="large"
                    type="portrait"
                    aspect="9:16"
                    onAddImage={(file) => {
                      handleData("portraitImage", "", file);
                      handleData("removePortraitImage", false);
                      clearFieldError("portraitImage");
                    }}
                    onDeleteImage={() => {
                      handleData("portraitImage", "");
                      handleData("removePortraitImage", true);
                    }}
                    hasDelete
                    isActive={Boolean(payload?.isActive)}
                    disabled={isReadOnly}
                    hasError={Boolean(fieldErrors?.["portraitImage"])}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="loyalty-level-status game-form-switcher">
            <SwitchBtn
              activeTxt={t("active")}
              notActiveTxt={t("hidden")}
              isActive={!!payload?.isActive}
              handleClick={() => {
                handleData(
                  "isActive",
                  payload?.isActive ? !payload?.isActive : true,
                );
                clearFieldError("isActive");
              }}
              hasError={Boolean(fieldErrors?.["isActive"])}
              disabled={isReadOnly}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoyaltyProgramLevelsMainTab;
